<template>
  <div style="position: relative;" class="bg-white pl-4 pt-4 rounded">
    <!-- Header with Back Button -->
    <div class="d-flex justify-content-between" style="margin-bottom: 1.70rem;">
      <div class="d-flex" @click="goBack()">
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="38" viewBox="0 0 40 38" fill="none" style="cursor: pointer;">
          <path
            d="M38.75 19.0003H1.25M1.25 19.0003L18.9583 1.29199M1.25 19.0003L18.9583 36.7087"
            stroke="#FF7C48"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <div class="d-flex align-items-center" style="margin-left: 1.25rem;">
          <div>
            <h1 class="title">{{ $t("audit-control.audit.detail") }}</h1>
          </div>
        </div>
      </div>
      <div>
        <!-- Lifecycle Actions -->
        <b-button v-if="audit.status === 'PLANNED'" variant="success" class="mr-2" @click="updateStatus('IN_PROGRESS')">
          <i class="bx bx-play font-size-16 align-middle"></i> {{ $t("audit-control.audit.start") }}
        </b-button>
        <b-button v-if="audit.status === 'IN_PROGRESS'" variant="primary" class="mr-2" @click="updateStatus('COMPLETED')">
          <i class="bx bx-check-double font-size-16 align-middle"></i> {{ $t("audit-control.audit.complete") }}
        </b-button>

        <!-- Edit Actions -->
        <div class="d-inline-block">
          <div v-if="!isEditMode">
            <b-button variant="primary" v-if="localAudit && localAudit.status != 'COMPLETED'" @click="enableEditMode">
              <i class="bx bx-pencil font-size-16 align-middle"></i> {{ $t("common.edit") }}
            </b-button>
          </div>
          <div v-else>
            <b-button variant="success" class="mr-2" @click="save"> <i class="bx bx-check font-size-16 align-middle"></i> {{ $t("common.save") }} </b-button>
            <b-button variant="danger" @click="cancelEditMode"> <i class="bx bx-x font-size-16 align-middle"></i> {{ $t("common.cancel") }} </b-button>
          </div>
        </div>
      </div>
    </div>

    <content-loading v-if="isLoading" />
    <div v-else class="pr-4 pb-4">
      <!-- Summary Header Section -->
      <div class="bg-light p-4 rounded mb-4 border relative" style="background-color: #e2e2e2; position: relative;">
        <!-- Status Badge - Top Right -->
        <div class="d-flex justify-content-end mb-3">
          <b-badge :variant="statusVariant" class="font-size-14">{{ audit.status ? $t("audit-control.audit.status." + audit.status.toLowerCase()) : "" }}</b-badge>
        </div>

        <div class="row mb-2">
          <!-- Col 1 -->
          <div class="col-md-6">
            <div class="mb-2">
              <span class="font-weight-bold">{{ $t("audit-control.audit.no") }} :</span>
              <span>{{ audit.no }}</span>
            </div>
            <div class="mb-2">
              <span class="font-weight-bold">{{ $t("audit-control.audit.name") }} :</span>
              <span v-if="!isEditMode">{{ audit.name | translate }}</span>
              <sweet-input v-else v-model="localAudit.name" :disableValidation="true" class="mt-1" />
            </div>
          </div>
          <!-- Col 2 -->
          <div class="col-md-3">
            <div class="mb-2">
              <span class="font-weight-bold">{{ $t("audit-control.audit.start-date") }} :</span>
              <span v-if="!isEditMode">{{ audit.startDate | date }}</span>
              <input v-else type="date" class="form-control form-control-sm mt-1" v-model="localAudit.startDate" />
            </div>
          </div>
          <!-- Col 3 -->
          <div class="col-md-3">
            <div class="mb-2">
              <span class="font-weight-bold">{{ $t("audit-control.audit.end-date") }} :</span>
              <span v-if="!isEditMode">{{ audit.endDate | date }}</span>
              <input v-else type="date" class="form-control form-control-sm mt-1" v-model="localAudit.endDate" />
            </div>
          </div>
        </div>

        <!-- Leader & Auditors -->
        <div class="row mt-3">
          <div class="col-md-6">
            <span class="font-weight-bold">{{ $t("audit-control.audit.leader") }} :</span>
            <div v-if="!isEditMode && audit.leader" class="d-flex align-items-center mt-1">
              <!-- <letter-avatar :name="audit.leader.name" :image="audit.leader.avatar" variant="primary" class="mr-2" size="30" /> -->
              <span>{{ audit.leader.name }}</span>
            </div>
            <organization-member-select v-else-if="isEditMode" :selected="localAudit.leader" @onSelect="localAudit.leader = $event" class="mt-1" />
            <span v-else class="text-muted ml-2">-</span>
          </div>
          <div class="col-md-6">
            <span class="font-weight-bold">{{ $t("audit-control.audit.auditors") }} :</span>
            <div v-if="!isEditMode && audit.auditors && audit.auditors.length" class="mt-1">
              <div v-for="auditor in audit.auditors" :key="auditor._id" class="d-inline-flex align-items-center mr-3 mb-1">
                <!-- <letter-avatar :name="auditor.name" :image="auditor.avatar" variant="secondary" class="mr-1" size="24" /> -->
                <span class="">{{ auditor.name }}</span>
              </div>
            </div>
            <organization-member-select v-else-if="isEditMode" :selected="localAudit.auditors" :multiple="true" @onSelect="localAudit.auditors = $event" class="mt-1" />
            <span v-else class="text-muted ml-2">-</span>
          </div>
        </div>

        <!-- Resources -->
        <div class="row mt-3">
          <div class="col-md-6">
            <span class="font-weight-bold">{{ $t("audit-control.audit.planned-resource") }} :</span>
            <span v-if="!isEditMode" class="ml-2">{{ audit.plannedResource | translate }}</span>
            <sweet-input v-else v-model="localAudit.plannedResource" :disableValidation="true" class="mt-1" />
          </div>
          <div class="col-md-6">
            <div v-if="audit.status === 'COMPLETED'">
              <span class="font-weight-bold">{{ $t("audit-control.audit.real-resource") }} :</span>
              <span class="ml-2">{{ audit.realResource | translate }}</span>
            </div>
          </div>
        </div>

        <!-- Description -->
        <div class="row mt-3 mb-4">
          <div class="col-md-12">
            <span class="font-weight-bold">{{ $t("audit-control.audit.description") }} :</span>
            <p v-if="!isEditMode" class="mb-0 mt-1 pl-2">{{ audit.description | translate }}</p>
            <sweet-textarea v-else v-model="localAudit.description" :rows="3" class="mt-2" />
          </div>
        </div>
      </div>

      <!-- Real Resource Modal -->
      <b-modal
        id="real-resource-modal"
        :title="$t('audit-control.audit.complete')"
        @ok="confirmComplete"
        ok-variant="primary"
        :ok-title="$t('common.save')"
        :cancel-title="$t('common.cancel')"
      >
        <p>{{ $t("audit-control.audit.enter-real-resource") }}</p>
        <sweet-input :disableValidation="true" v-model="completionRealResource" :label="$t('audit-control.audit.real-resource')" layout="vertical" />
      </b-modal>

      <div class="mb-4">
        <div class="d-flex justify-content-between align-items-center bg-info text-white p-3 rounded-top">
          <h5 class="m-0">{{ $t("audit-control.audit.scope") }} ({{ localAudit.controls ? localAudit.controls.length : 0 }})</h5>
          <div>
            <b-button variant="light" size="sm" class="text-info font-weight-bold mr-2" @click="showFilters = !showFilters">
              <i class="bx bx-filter"></i> {{ $t("common.filter") }}
            </b-button>

            <b-button v-if="audit.status === 'IN_PROGRESS'" variant="light" size="sm" class="text-info font-weight-bold" @click="$bvModal.show('control-modal-select')">
              <i class="bx bx-plus"></i> {{ $t("common.add") }}
            </b-button>
          </div>
        </div>

        <div class="bg-white border border-top-0 p-3 rounded-bottom shadow-sm">
          <div class="d-flex">
            <div v-if="showFilters" class="mr-3 border-right pr-3" style="width: 280px; flex-shrink: 0;">
              <AuditControlFilterSidebar />
            </div>

            <div class="flex-grow-1 ml-2" style="min-width: 0;">
              <b-tabs v-model="activeTab" content-class="mt-3">
                <b-tab active>
                  <template #title>
                    {{ $t("audit-control.tab.ongoing") }} <b-badge variant="primary" class="ml-1">{{ ongoingPagination.totalDocs }}</b-badge>
                  </template>
                  <content-loading v-if="ongoingPagination.loading" />
                  <div v-else>
                    <table class="table table-bordered table-striped table-hover">
                      <thead class="bg-soft-info">
                        <tr>
                          <th>{{ $t("audit-control.control.no") }}</th>
                          <th>{{ $t("audit-control.control.name") }}</th>
                          <th>{{ $t("audit-control.control.category") }}</th>
                          <th>{{ $t("audit-control.control.status") }}</th>
                          <th>{{ $t("audit-control.control.last-test") }}</th>
                          <th style="width: 50px;"></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="control in ongoingControls" :key="control._id" @click="openControl(control)" class="cursor-pointer">
                          <td>{{ control.no }}</td>
                          <td>{{ control.name | translate }}</td>
                          <td>
                            <span v-if="control.category">{{ control.category.name | translate }}</span>
                          </td>
                          <td>
                            <!-- If it has a completed test in this context, show COMPLETED. Else show definition status -->
                            <span v-if="control.auditTestContext && control.auditTestContext.length > 0 && control.auditTestContext[0].status === 'COMPLETED'">
                              <b-badge variant="success">{{ $t("audit-control.status.completed") }}</b-badge>
                            </span>
                            <span v-else-if="control.status && control.status !== 'COMPLETED'">
                              {{ $t("audit-control.status." + control.status.toLowerCase()) }}
                            </span>
                            <span v-else>
                              {{ $t("audit-control.status.active") }}
                            </span>
                          </td>
                          <td>
                            <div v-if="control.lastTest" class="small">
                              <div class="font-weight-bold">{{ control.lastTest.auditName | translate }}</div>
                              <div>{{ control.lastTest.testDate | date }}</div>
                              <div v-if="control.lastTest.result">
                                <b-badge :variant="control.lastTest.result === 'EFFECTIVE' ? 'success' : 'danger'">
                                  {{ $t("audit-control.test.result." + control.lastTest.result.toLowerCase()) }}
                                </b-badge>
                                <span class="ml-1 text-muted" v-if="control.lastTest.findingCount > 0">
                                  ({{ control.lastTest.findingCount }} {{ $t("audit-control.finding.title") }})
                                </span>
                              </div>
                            </div>
                            <span v-else class="text-muted">-</span>
                          </td>
                          <td class="text-center" @click.stop>
                            <i v-if="isEditMode || audit.status === 'PLANNED'" class="bx bx-trash hand text-danger font-size-18" @click="removeControl(control)"></i>
                          </td>
                        </tr>
                        <tr v-if="!ongoingControls.length">
                          <td colspan="5" class="text-center">{{ $t("common.no-data") }}</td>
                        </tr>
                      </tbody>
                    </table>
                    <div v-if="ongoingPagination.totalDocs > 0" class="d-flex justify-content-center mt-3">
                      <b-pagination
                        v-model="ongoingPagination.page"
                        :total-rows="ongoingPagination.totalDocs"
                        :per-page="ongoingPagination.limit"
                        aria-controls="ongoing-table"
                      ></b-pagination>
                    </div>
                  </div>
                </b-tab>

                <!-- Completed Controls Tab -->
                <b-tab>
                  <template #title>
                    {{ $t("audit-control.tab.completed") }} <b-badge variant="success" class="ml-1">{{ completedPagination.totalDocs }}</b-badge>
                  </template>
                  <content-loading v-if="completedPagination.loading" />
                  <div v-else>
                    <table class="table table-bordered table-striped table-hover">
                      <thead class="bg-soft-success">
                        <tr>
                          <th>{{ $t("audit-control.control.no") }}</th>
                          <th>{{ $t("audit-control.control.name") }}</th>
                          <th>{{ $t("audit-control.control.category") }}</th>
                          <th>{{ $t("audit-control.control.status") }}</th>
                          <th>{{ $t("audit-control.control.last-test") }}</th>
                          <th style="width: 50px;"></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="control in completedControls" :key="control._id" @click="openControl(control)" class="cursor-pointer">
                          <td>{{ control.no }}</td>
                          <td>{{ control.name | translate }}</td>

                          <td>
                            <span v-if="control.category">{{ control.category.name | translate }}</span>
                          </td>
                          <td>
                            <!-- If it has a completed test in this context, show COMPLETED. Else show definition status -->
                            <span v-if="control.auditTestContext && control.auditTestContext.length > 0 && control.auditTestContext[0].status === 'COMPLETED'">
                              <b-badge variant="success">{{ $t("audit-control.status.completed") }}</b-badge>
                            </span>
                            <span v-else-if="control.status && control.status !== 'COMPLETED'">
                              {{ $t("audit-control.status." + control.status.toLowerCase()) }}
                            </span>
                            <span v-else>
                              {{ $t("audit-control.status.active") }}
                            </span>
                          </td>
                          <td>
                            <div v-if="control.lastTest" class="small">
                              <div class="font-weight-bold">{{ control.lastTest.auditName | translate }}</div>
                              <div>{{ control.lastTest.testDate | date }}</div>
                              <div v-if="control.lastTest.result">
                                <b-badge :variant="control.lastTest.result === 'EFFECTIVE' ? 'success' : 'danger'">
                                  {{ $t("audit-control.test.result." + control.lastTest.result.toLowerCase()) }}
                                </b-badge>
                                <span class="ml-1 text-muted" v-if="control.lastTest.findingCount > 0">
                                  ({{ control.lastTest.findingCount }} {{ $t("audit-control.finding.title") }})
                                </span>
                              </div>
                            </div>
                            <span v-else class="text-muted">-</span>
                          </td>
                          <td class="text-center" @click.stop>
                            <i v-if="isEditMode || audit.status === 'PLANNED'" class="bx bx-trash hand text-danger font-size-18" @click="removeControl(control)"></i>
                          </td>
                        </tr>
                        <tr v-if="!completedControls.length">
                          <td colspan="5" class="text-center">{{ $t("common.no-data") }}</td>
                        </tr>
                      </tbody>
                    </table>
                    <div v-if="completedPagination.totalDocs > 0" class="d-flex justify-content-center mt-3">
                      <b-pagination
                        v-model="completedPagination.page"
                        :total-rows="completedPagination.totalDocs"
                        :per-page="completedPagination.limit"
                        aria-controls="completed-table"
                      ></b-pagination>
                    </div>
                  </div>
                </b-tab>
              </b-tabs>
            </div>
          </div>
        </div>
      </div>

      <!-- Use the new advanced modal for adding controls -->
      <AuditControlSelectModal
        id="control-modal-select"
        :title="$t('audit-control.control.select')"
        :pre-selected-ids="localAudit.controls.map((c) => c._id)"
        :pre-selected-objects="localAudit.controls"
        @selected="syncControls"
      />
    </div>
  </div>
</template>

<script>
import SweetInput from "@c/form/sweet-input";
import SweetTextarea from "@c/form/sweet-textarea";
import OrganizationMemberSelect from "@cc/organization-member-select";
import AuditControlFilterSidebar from "./audit-control-filter-sidebar.vue";
import AuditControlSelectModal from "./audit-control-select-modal.vue";

import { debounce } from "lodash";

export default {
  components: { SweetTextarea, AuditControlSelectModal, OrganizationMemberSelect, SweetInput, AuditControlFilterSidebar },
  data() {
    return {
      showFilters: false,
      isEditMode: false,
      localAudit: {
        name: null,
        startDate: null,
        endDate: null,
        description: null,
        controls: [],
      },
      ongoingPagination: {
        page: 1,
        limit: 10,
        totalDocs: 0,
        loading: false,
        docs: [],
      },
      completedPagination: {
        page: 1,
        limit: 10,
        totalDocs: 0,
        loading: false,
        docs: [],
      },
      activeTab: 0,

      completionRealResource: null,
    };
  },
  computed: {
    isLoading() {
      return this.$store.getters["organizationAudit/isLoading"];
    },
    audit() {
      return this.$store.getters["organizationAudit/selectedEntity"] || {};
    },
    statusVariant() {
      switch (this.audit.status) {
        case "IN_PROGRESS":
          return "primary";
        case "COMPLETED":
          return "success";
        case "CANCELLED":
          return "danger";
        default:
          return "warning"; // PLANNED
      }
    },
    ongoingControls() {
      return this.ongoingPagination.docs;
    },
    completedControls() {
      return this.completedPagination.docs;
    },
    // complianceCoverage() {
    //   // Note: complianceCoverage currently relies on localAudit.controls (all controls)
    //   // If we paginate, localAudit.controls might be empty or incomplete!
    //   // This is a trade-off. We might need to fetch ALL controls (ids/compliance) for stats separately,
    //   // OR accept stats only show current page (bad), OR accept localAudit.controls still holds all IDs but details are fetched.
    //   // `findById` returns Audit with `controls` array populated (usually).
    //   // If we switch to server-side loaded controls for List, does `localAudit.controls` still exist?
    //   // `fetchDetails` calls `organizationAudit/findById`.
    //   // The Model `findById` usually populates `controls`.
    //   // If `controls` is large (thousands), `findById` will be heavy.
    //   // But for now, we assume `findById` still loads them.
    //   // So `complianceCoverage` can still use `this.localAudit.controls`.
    //   // However, `ongoingControls` and `completedControls` (View) will use `ongoingPagination`.

    //   const stats = {};
    //   if (!this.localAudit.controls) return [];

    //   this.localAudit.controls.forEach((control) => {
    //     if (!control.complianceItems) return;
    //     control.complianceItems.forEach((item) => {
    //       const standard = item.compliance || "Other";
    //       if (!stats[standard]) {
    //         stats[standard] = {
    //           name: standard,
    //           items: new Set(),
    //           details: [],
    //         };
    //       }

    //       // Add detailed info for the popover
    //       stats[standard].details.push({
    //         itemId: item._id,
    //         itemNo: item.no,
    //         itemName: item.name,
    //         controlName: control.name,
    //       });

    //       // Track unique items
    //       stats[standard].items.add(item._id);
    //     });
    //   });

    //   // Convert to array and sort by unique count desc
    //   return Object.values(stats)
    //     .map((s) => ({
    //       ...s,
    //       uniqueCount: s.items.size,
    //       // Sort details by item No for better readability
    //       details: s.details.sort((a, b) => (a.itemNo || "").localeCompare(b.itemNo || "")),
    //     }))
    //     .sort((a, b) => b.uniqueCount - a.uniqueCount);
    // },
    controlFilters() {
      return this.$store.getters["organizationControl/controlFilters"] || {};
    },
  },
  watch: {
    audit: {
      handler(val) {
        if (val) {
          this.syncLocalAudit(val);
          if (val._id) {
            this.fetchActiveTabData();
          }
        }
      },
      immediate: true,
      deep: true,
    },
    controlFilters: {
      handler: debounce(function() {
        this.ongoingPagination.page = 1;
        this.completedPagination.page = 1;
        this.fetchActiveTabData();
      }, 500),
      deep: true,
    },
    activeTab() {
      this.fetchActiveTabData();
    },
    "ongoingPagination.page"() {
      if (this.activeTab === 0) {
        this.fetchOngoingControls();
      }
    },
    "completedPagination.page"() {
      if (this.activeTab === 1) {
        this.fetchCompletedControls();
      }
    },
  },
  methods: {
    goBack() {
      this.$router.push("/audit-control/audit");
    },
    syncLocalAudit(val) {
      this.localAudit = this.prepareFormForEdit(val);
      // Handle Date conversion for input type=date
      if (this.localAudit.startDate) this.localAudit.startDate = new Date(this.localAudit.startDate).toISOString().split("T")[0];
      if (this.localAudit.endDate) this.localAudit.endDate = new Date(this.localAudit.endDate).toISOString().split("T")[0];
      // Use spread to ensure we have a fresh copy of the array
      this.localAudit.controls = val.controls ? [...val.controls] : [];
      if (!this.localAudit.auditors) this.localAudit.auditors = [];
    },
    fetchActiveTabData() {
      if (this.activeTab === 0) {
        this.fetchOngoingControls();
      } else if (this.activeTab === 1) {
        this.fetchCompletedControls();
      }
    },
    async fetchOngoingControls() {
      if (!this.audit._id) return;
      this.ongoingPagination.loading = true;
      try {
        const query = {
          auditId: { value: this.audit._id, isObject: true },
          organization: { value: this.audit.organization, isObject: true },
          ongoing: { value: true, isObject: true },
        };
        const options = {
          page: this.ongoingPagination.page,
          limit: this.ongoingPagination.limit,
          sort: { createdAt: -1 },
        };

        const response = await this.$store.dispatch("organizationControl/fetchFilteredControls", { query, options });
        this.ongoingPagination.docs = response.data.docs;
        this.ongoingPagination.totalDocs = response.data.totalDocs;
      } catch (e) {
        console.error(e);
      } finally {
        this.ongoingPagination.loading = false;
      }
    },
    async fetchCompletedControls() {
      if (!this.audit._id) return;
      this.completedPagination.loading = true;
      try {
        const query = {
          auditId: { value: this.audit._id, isObject: true },
          organization: { value: this.audit.organization, isObject: true },
          status: { value: "COMPLETED", isObject: true },
        };
        const options = {
          page: this.completedPagination.page,
          limit: this.completedPagination.limit,
          sort: { createdAt: -1 },
        };

        const response = await this.$store.dispatch("organizationControl/fetchFilteredControls", { query, options });
        this.completedPagination.docs = response.data.docs;
        this.completedPagination.totalDocs = response.data.totalDocs;
      } catch (e) {
        console.error(e);
      } finally {
        this.completedPagination.loading = false;
      }
    },
    enableEditMode() {
      this.syncLocalAudit(this.audit);
      this.isEditMode = true;
    },
    cancelEditMode() {
      this.syncLocalAudit(this.audit);
      this.isEditMode = false;
    },
    async save() {
      try {
        if (this.localAudit?.status == "COMPLETED") {
          return;
        }

        const payload = {
          ...this.localAudit,
          controls: this.localAudit.controls.map((c) => c._id || c),
          leader: this.localAudit.leader ? this.localAudit.leader._id || this.localAudit.leader : undefined,
          auditors: this.localAudit.auditors ? this.localAudit.auditors.map((a) => a._id || a) : [],
        };
        await this.$store.dispatch("organizationAudit/update", payload);
        this.$toast.success(this.$t("common.success"));
        this.isEditMode = false;
        await this.fetchDetails();
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    updateStatus(status) {
      if (status === "COMPLETED") {
        this.completionRealResource = null;
        this.$bvModal.show("real-resource-modal");
        return;
      }
      this.performStatusUpdate(status);
    },
    async confirmComplete(bvModalEvt) {
      this.performStatusUpdate("COMPLETED", { realResource: this.completionRealResource });
    },
    async performStatusUpdate(status, extraFields = {}) {
      try {
        const payload = {
          ...this.localAudit,
          ...extraFields,
          _id: this.audit._id,
          status: status,
          controls: this.localAudit.controls.map((c) => c._id || c),
          leader: this.localAudit.leader ? this.localAudit.leader._id || this.localAudit.leader : undefined,
          auditors: this.localAudit.auditors ? this.localAudit.auditors.map((a) => a._id || a) : [],
        };
        await this.$store.dispatch("organizationAudit/update", payload);
        this.$toast.success(this.$t("common.success"));
        await this.fetchDetails();
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    openControl(control) {
      this.$router.push({
        name: "organizationAuditControlDetail",
        params: { id: this.audit._id, controlId: control._id },
      });
    },
    syncControls(items) {
      if (!items || !Array.isArray(items)) return;
      this.localAudit.controls = [...items];
      this.save();
    },
    removeControl(control) {
      if (!this.localAudit.controls) return;
      const idx = this.localAudit.controls.findIndex((c) => {
        const id = c._id || c;
        return id.toString() === control._id.toString();
      });
      if (idx > -1) {
        this.localAudit.controls.splice(idx, 1);
        if (!this.isEditMode) {
          this.save();
        }
      }
    },
    async fetchDetails() {
      if (this.$route.params.id) {
        await this.$store.dispatch("organizationAudit/findById", this.$route.params.id);
      }
    },
  },
  async mounted() {
    await this.fetchDetails();
    await this.fetchCompletedControls();
  },
};
</script>

<style scoped>
.custom-accordion .card-header {
  padding: 0.75rem 1.25rem;
  border-bottom: 0;
}
.custom-accordion .card-header a {
  text-decoration: none;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
